import { h, Host, forceUpdate } from '@stencil/core';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import sumBy from 'lodash/sumBy';
import { VIEW_ON_PLACEHOLDER, EQdsButtonImportance, EQdsButtonSize } from '@pes-ui/utils';
import { isBrowser, isMobile } from '../../../globals/utils';
import { isTopRunner } from '../guards';
import RouterLinkView from '../router-link-view';
import { isAppRoutingEnabled } from '../../pes-router/utils';
const SIZE_EXTRA_POINT_STEP = 20;
export class Card {
  constructor() {
    this.commercialStatusBadgeRef = null;
    this.badgeRef = null;
    this.createEllipsisRef = (el) => {
      if (el) {
        this.ellipsisRef = el;
      }
    };
    this.setSizeExtraPoints = () => {
      if (this.size !== 'small' && this.isMobile) {
        const badgesSize = sumBy([this.commercialStatusBadgeRef, this.badgeRef], this.getComputedHeight);
        this.sizeExtraPoints = this.sizeToExtraPoints(badgesSize);
      }
    };
    this.getComputedHeight = (el) => {
      var _a, _b;
      return ((_b = (_a = el === null || el === void 0 ? void 0 : el.getBoundingClientRect) === null || _a === void 0 ? void 0 : _a.call(el)) === null || _b === void 0 ? void 0 : _b.height) || 0;
    };
    this.page = undefined;
    this.item = undefined;
    this.viewOnLabel = undefined;
    this.labelTemplate = undefined;
    this.labelPlaceholder = undefined;
    this.size = undefined;
    this.breadcrumbs = undefined;
    this.storageName = undefined;
    this.isMobile = isMobile();
    this.sizeExtraPoints = 0;
    this.el.dir = document.documentElement.dir || 'auto';
  }
  handleResize() {
    this.isMobile = isMobile();
  }
  componentDidUpdate() {
    var _a;
    if (isBrowser()) {
      (_a = this.ellipsisRef) === null || _a === void 0 ? void 0 : _a.triggerUpdate();
      this.setSizeExtraPoints();
    }
  }
  renderLabel() {
    var _a;
    return isTopRunner(this.item) && ((_a = this.item) === null || _a === void 0 ? void 0 : _a.redirectUrl)
      ? this.renderRedirectLabel()
      : this.renderCounterLabel();
  }
  sizeToExtraPoints(size) {
    const points = Math.round(size / SIZE_EXTRA_POINT_STEP) - 1;
    return points > 0 ? points : 0;
  }
  componentDidLoad() {
    forceUpdate(this.el);
  }
  renderRedirectLabel() {
    var _a;
    const domainName = isTopRunner(this.item) ? (_a = this.item) === null || _a === void 0 ? void 0 : _a.domainName : '';
    return (h("div", { class: "card__label", title: this.getViewOnLabel(domainName) }, h("div", { class: "card__text-wrapper" }, h("qds-standalone-link", { size: EQdsButtonSize.SMALL, importance: EQdsButtonImportance.STANDARD, class: "range-card__view-prod-label", iconName: "arrow-top-right-on-square" }, `${this.getViewOnLabel()} ${domainName}`))));
  }
  getViewOnLabel(domainName = '') {
    return this.viewOnLabel.replace(VIEW_ON_PLACEHOLDER, domainName);
  }
  renderCounterLabel() {
    var _a;
    return (h("div", { class: "card__label", innerHTML: ((_a = this.item) === null || _a === void 0 ? void 0 : _a.count) > 0 ? this.getCounterLabel() : '&nbsp;' }));
  }
  getCounterLabel() {
    var _a, _b;
    return (_a = this.labelTemplate) === null || _a === void 0 ? void 0 : _a.replace(this.labelPlaceholder, String((_b = this.item) === null || _b === void 0 ? void 0 : _b.count)).replace('&nbsp;', ' ');
  }
  hasCommercialStatus(item) {
    return !isEmpty(item === null || item === void 0 ? void 0 : item.commercialStatus) && !!item.commercialStatus.label;
  }
  hasBadge(item) {
    var _a;
    return !!((_a = item === null || item === void 0 ? void 0 : item.badge) === null || _a === void 0 ? void 0 : _a.label);
  }
  hasBrandImage(item) {
    return isTopRunner(item) && !!item.brandLogo;
  }
  hasAdditionalInfo(item) {
    return this.hasBadge(item) || this.hasBrandImage(item) || this.hasCommercialStatus(item);
  }
  renderTitle() {
    var _a, _b;
    if (this.isMobile && isBrowser()) {
      return (h("h2", { class: "card__title" }, h("pes-ellipsis", { maxHeight: "32px", ref: this.createEllipsisRef, content: (_a = this.item) === null || _a === void 0 ? void 0 : _a.name, wrapperModifier: "centered" })));
    }
    return h("h2", { class: "card__title", innerHTML: (_b = this.item) === null || _b === void 0 ? void 0 : _b.name });
  }
  render() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
    const showDescription = Boolean((_a = this.item) === null || _a === void 0 ? void 0 : _a.description);
    const redirectUrl = isTopRunner(this.item) ? (_b = this.item) === null || _b === void 0 ? void 0 : _b.redirectUrl : undefined;
    const url = (_c = this.item) === null || _c === void 0 ? void 0 : _c.url;
    const isImageAnimated = !showDescription && this.size !== 'large';
    return (h(Host, { class: cn('card', {
        'mobile-gallery-slide': this.isMobile && isBrowser(),
        'card--small': this.isMobile && this.size === 'small',
        'card--large': (!this.isMobile || !isBrowser()) && this.size === 'large',
      }) }, h(RouterLinkView, { page: this.page, pageId: (_d = this.item) === null || _d === void 0 ? void 0 : _d.sourceId, url: isAppRoutingEnabled() ? '' : redirectUrl || url, target: redirectUrl ? '_blank' : '_self', linkClassName: cn(`card__link${!isBrowser() ? ' server_render' : ''}`, this.sizeExtraPoints && `card__link--size-extra-points-${this.sizeExtraPoints}`), name: (_e = this.item) === null || _e === void 0 ? void 0 : _e.name, pageName: this.page.toLocaleUpperCase(), storageName: this.storageName, breadcrumbs: this.breadcrumbs }, h("div", { class: "card__link-text-content" }, this.renderTitle(), this.renderLabel(), showDescription && (h("div", { class: "card__description" }, h("pes-ellipsis", { maxHeight: this.size === 'large' ? '100px' : '120px', ref: this.createEllipsisRef, content: (_f = this.item) === null || _f === void 0 ? void 0 : _f.description }))), this.hasAdditionalInfo(this.item) && (h("div", { class: "card__additional-info" }, this.hasBrandImage(this.item) && (h("pes-lazy-img", { class: "card__brand-image card__brand-image--small-mobile-hidden card__additional-info-item", src: (_g = this.item) === null || _g === void 0 ? void 0 : _g.brandLogo.absoluteUrl, alt: (_j = (_h = this.item) === null || _h === void 0 ? void 0 : _h.brandLogo.alt) !== null && _j !== void 0 ? _j : '' })), this.hasCommercialStatus(this.item) && (h("pes-badge-wrapper", { ref: (el) => (this.commercialStatusBadgeRef = el), class: "card__badge card__additional-info-item", type: (_l = (_k = this.item) === null || _k === void 0 ? void 0 : _k.commercialStatus) === null || _l === void 0 ? void 0 : _l.status, label: (_o = (_m = this.item) === null || _m === void 0 ? void 0 : _m.commercialStatus) === null || _o === void 0 ? void 0 : _o.label, color: (_q = (_p = this.item) === null || _p === void 0 ? void 0 : _p.commercialStatus) === null || _q === void 0 ? void 0 : _q.color })), this.hasBadge(this.item) && (h("pes-badge-wrapper", { ref: (el) => (this.badgeRef = el), class: "card__badge card__additional-info-item", type: (_s = (_r = this.item) === null || _r === void 0 ? void 0 : _r.badge) === null || _s === void 0 ? void 0 : _s.type, label: (_u = (_t = this.item) === null || _t === void 0 ? void 0 : _t.badge) === null || _u === void 0 ? void 0 : _u.label }))))), h("picture", { class: cn('card__picture', {
        'card__picture--animate': isImageAnimated,
      }) }, h("pes-lazy-img", { src: (_v = this.item) === null || _v === void 0 ? void 0 : _v.pictureUrl, alt: "" })), this.hasBrandImage(this.item) && (h("pes-lazy-img", { class: "card__brand-image card__brand-image--small-mobile", src: (_w = this.item) === null || _w === void 0 ? void 0 : _w.brandLogo.absoluteUrl, alt: (_y = (_x = this.item) === null || _x === void 0 ? void 0 : _x.brandLogo.alt) !== null && _y !== void 0 ? _y : '' })))));
  }
  static get is() { return "pes-card"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["card.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["card.css"]
    };
  }
  static get properties() {
    return {
      "page": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "PesPageType",
          "resolved": "\"\" | \"all-products\" | \"category\" | \"comparator\" | \"product\" | \"product-cart\" | \"range\" | \"subcategory\"",
          "references": {
            "PesPageType": {
              "location": "import",
              "path": "../../../globals/types"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [{
              "name": "example",
              "text": "range."
            }],
          "text": "Application page."
        },
        "attribute": "page",
        "reflect": false
      },
      "item": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TCardItem",
          "resolved": "{ brandImageUrl?: string; brandImageAltText?: string; badge?: Badge; count?: number; description?: string; domainName?: string; id?: string; longDescription?: string; name?: string; pictureUrl?: string; redirectUrl?: string; simpleName?: string; smallPictureUrl?: string; sourceId?: string; type?: string; url?: string; commercialStatus?: CommercialStatus; brandLogo?: TBrandLogo; } | { url?: string; count?: number; description?: string; id?: string; longDescription?: string; name?: string; pictureUrl?: string; simpleName?: string; smallPictureUrl?: string; sourceId?: string; type?: string; badge?: Badge; commercialStatus?: CommercialStatus; brandImageUrl?: string; }",
          "references": {
            "TCardItem": {
              "location": "import",
              "path": "./types"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Top Category or Top Runner item's data."
        }
      },
      "viewOnLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "view-on-label",
        "reflect": false
      },
      "labelTemplate": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "label-template",
        "reflect": false
      },
      "labelPlaceholder": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "label-placeholder",
        "reflect": false
      },
      "size": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "'small' | 'large' | ''",
          "resolved": "\"\" | \"large\" | \"small\"",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [{
              "name": "example",
              "text": "'small'."
            }],
          "text": "Card size."
        },
        "attribute": "size",
        "reflect": false
      },
      "breadcrumbs": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TBreadcrumbs[]",
          "resolved": "TBreadcrumbs[]",
          "references": {
            "TBreadcrumbs": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "breadcrumbs array for saving in indexDB"
        }
      },
      "storageName": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "storageName in indexDB for saving breadcrumbs"
        },
        "attribute": "storage-name",
        "reflect": false
      }
    };
  }
  static get states() {
    return {
      "isMobile": {},
      "sizeExtraPoints": {}
    };
  }
  static get elementRef() { return "el"; }
  static get listeners() {
    return [{
        "name": "resize",
        "method": "handleResize",
        "target": "window",
        "capture": false,
        "passive": true
      }];
  }
}
